import React, { useCallback } from 'react';
import { Icon, Dimmer, Loader, Popup } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';
import EditAnatomicRegionModal from 'app/components/Dropzone/EditAnatomicRegionModal';
import ImageToPACSSyncStatus from 'app/components/ImageToPACSSyncStatus';
import { ThumbnailImageRenderer } from 'app/components/ThumbnailImageRenderer';
import { SpeciesType } from 'app/constants/species';
import ExamIcon from 'app/components/ExamIcon';
import { useSelector } from 'react-redux';
import {
  isPACSConfigurationDisabled,
  selectPACSConfiguration,
} from 'app/redux/PACSConfiguration/reducer';
import { ViewerImage } from 'app/components/Viewer/types';
import isWorkListImage from 'app/utils/isWorkListImage';
import { isAcquisitionImage } from 'app/utils/isAcquisitionImage';
import { selectViewerConfiguration } from 'app/redux/viewerConfiguration/reducer';

type Props = {
  image: ViewerImage;
  specie?: SpeciesType;
  onNewAnatomicRegion: (imageId: string, newAnatomicRegion: string) => void;
  onRedo: (imageId: string) => void;
  onDelete: (imageId: string) => void;
  onSelect: (imageId: string) => void;
};

export default React.memo(function Thumbnail({
  image,
  specie,
  onNewAnatomicRegion,
  onRedo,
  onDelete,
  onSelect,
}: Props) {
  const PACSConfiguration = useSelector(selectPACSConfiguration);
  const viewerConfiguration = useSelector(selectViewerConfiguration);
  const {
    _id: imageId,
    isImageLoading,
    isProcessingOngoing,
    loadError,
    isSelected,
    anatomicRegion,
  } = image;
  const shouldDisplayPACSStatus =
    !isPACSConfigurationDisabled(PACSConfiguration) && isAcquisitionImage(image);
  const isNoXRayDeletionEnabled = viewerConfiguration?.noXRayDeletion;

  const onDeleteCb = useCallback(
    (event: React.UIEvent) => {
      event.stopPropagation();
      onDelete(imageId);
    },
    [imageId, onDelete]
  );

  const onSelectCb = useCallback(
    (event: React.UIEvent) => {
      event.stopPropagation();
      onSelect(imageId);
    },
    [onSelect, imageId]
  );
  const onRedoCb = useCallback(
    (event: React.UIEvent) => {
      event.stopPropagation();
      onRedo(imageId);
    },
    [onRedo, imageId]
  );
  const onAnatomicRegionCb = useCallback(
    (newAnatomicRegion: string) => onNewAnatomicRegion(imageId, newAnatomicRegion),
    [onNewAnatomicRegion, imageId]
  );

  return (
    <div>
      <div
        tabIndex={0}
        role="button"
        onKeyDown={onSelectCb}
        onClick={onSelectCb}
        className={`thumbnail ${isSelected ? 'selected' : ''}`}
        data-testid="thumbnail-image"
      >
        {isWorkListImage(image) ? (
          <ExamIcon exam={anatomicRegion} />
        ) : (
          <div className="thumbnail-image-with-status">
            <Dimmer
              data-testid="thumbnail-render-area-loader"
              active={isImageLoading || isProcessingOngoing}
              style={{ zIndex: 0 }}
            >
              <Loader style={{ zIndex: 0 }} className="ui loader loaderPerformance" />
            </Dimmer>
            <ThumbnailImageRenderer
              image={image.displayableImage}
              viewport={image.viewport}
              annotations={image.annotations}
              className={`radio-image-container ${loadError ? 'hidden' : ''}`}
            />

            <div className="thumbnail-status">
              {shouldDisplayPACSStatus && (
                <ImageToPACSSyncStatus
                  isSync={image?.image_metadata?.PACS?.isSync}
                  isSyncOngoing={image?.PACS?.isSyncOngoing}
                  syncError={image?.PACS?.syncError}
                />
              )}
            </div>
            {loadError && (
              <div className="image-load-failure image-load-failure--thumbnail">
                <Icon name="warning sign" />
                <FormattedMessage id="dropzone.image_load.failure" />
              </div>
            )}
          </div>
        )}

        <div className="thumbnail-actions flex">
          {(isWorkListImage(image) || isAcquisitionImage(image)) && (
            <EditAnatomicRegionModal
              specie={specie}
              image={image}
              onNewAnatomicRegion={onAnatomicRegionCb}
            />
          )}

          {isAcquisitionImage(image) && (
            <Popup
              content={<FormattedMessage id="dropzone.redo_acquisition" />}
              trigger={
                <button
                  type="button"
                  className="thumbnail-icon large icon redo-image"
                  data-testid="redo-button"
                  onClick={onRedoCb}
                >
                  <Icon fitted name="redo alternate" />
                </button>
              }
            />
          )}
          {(!isNoXRayDeletionEnabled || isWorkListImage(image)) && (
            <button
              type="button"
              className="thumbnail-icon large icon delete-image"
              data-testid="delete-button"
              onClick={onDeleteCb}
            >
              <Icon fitted name="times" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
});
